import { BaseReactiveClass } from 'Classes/BaseReactiveClass';
import {
  getWalletData,
  deleteWalletFundingSource,
  postWalletFundingSource,
  getWalletTransaction,
  deleteWalletTransaction,
} from 'Api/wallet';
import { ModelAccountWalletBankAccount } from './ModelAccountWalletBankAccount';
import { ModelAccountWalletBalance } from './ModelAccountWalletBalance';
import { ModelAccountWalletBeneficialOwners } from './ModelAccountWalletBeneficialOwners';

export class ModelAccountWallet extends BaseReactiveClass {
  static INITIAL_LOAD_TRANSACTION_NUMBER = 25;

  constructor(data) {
    super(data);

    this.$set('$dataLoading', false);
    this.$set('$dataLoaded', false);
    this.$set('$deleteLoading', false);
    this.$set('$transactionsLoading', false);
    this.beneficialOwners = new ModelAccountWalletBeneficialOwners({ accountId: data.id });
    this.transactionOffset = 0;
  }

  static async fetchData(accountId) {
    return getWalletData(accountId);
  }

  set banks(banksData) {
    const banks = (banksData || []).map(bankAccountData => new ModelAccountWalletBankAccount(bankAccountData));
    this.$set('_banks', banks);
  }

  get banks() {
    return this._banks || [];
  }

  get isSomeLinkedBankAccount() {
    return Boolean(this.banks.length);
  }

  set balance(balanceData) {
    if (this.balance instanceof ModelAccountWalletBalance) {
      this.balance.$setAll(balanceData);
    } else {
      const balance = new ModelAccountWalletBalance(balanceData || {});
      this.$set('_balance', balance);
      this._balance = balance;
    }
  }

  get balance() {
    return this._balance || {};
  }

  get fundingSources() {
    return this.banks.concat(this.balance);
  }

  static async buildClass(accountId) {
    const data = await ModelAccountWallet.fetchData(accountId);
    return new ModelAccountWallet(data);
  }

  set transactionTotalCount(transactionTotalCount) {
    this.balance.total = transactionTotalCount;
  }

  get transactionTotalCount() {
    return this.balance.total || 0;
  }

  get isCanAddBankAccount() {
    return this.is_possibly && this.banks.length < 7;
  }

  get isCanWithdraw() {
    return Boolean(this.is_withdraw_possible);
  }

  set transactions(transactions) {
    this.balance.transactions = transactions;
  }

  get transactions() {
    return this.balance.transactions;
  }

  get transactionCount() {
    return this.transactions.length || 0;
  }

  get isTransactionLoadedAll() {
    return (this.transactionOffset + this.transactionCount) >= this.transactionTotalCount;
  }

  set pendingTransactions(transactions) {
    this.balance.pending_transactions = transactions;
  }

  get pendingTransactions() {
    return this.balance.pending_transactions;
  }

  $setAll(data, model = this) {
    const { balance, ...restData } = data || {};
    if (balance) {
      // set balance as plain object to create instance of AccountWalletBalance
      this.balance = balance;
    }
    return super.$setAll(restData, model);
  }

  async load() {
    if (this._loadPromise) return this._loadPromise;
    this.$dataLoading = true;
    try {
      this._loadPromise = ModelAccountWallet.fetchData(this.accountId);
      const data = await this._loadPromise;
      data.banks = data.banks || [];
      this.$setAll(data);
      this.$dataLoaded = true;
      return data;
    } finally {
      this._loadPromise = null;
      this.$dataLoading = false;
    }
  }

  loadOnce() {
    if (this.$dataLoaded) return Promise.resolve();
    return this.load();
  }

  async update() {
    return this.load();
  }

  // TODO: rename to deleteLinkedBankAccount
  delete(bankAccount) {
    return deleteWalletFundingSource(this.accountId, bankAccount);
  }

  // TODO: rename to addLinkedBankAccount
  async addFundingSource(fundingSourceData) {
    return postWalletFundingSource(this.accountId, fundingSourceData);
  }

  async loadMoreLessTransactions(offset) {
    this.$transactionsLoading = true;
    try {
      const data = await getWalletTransaction(this.accountId, ModelAccountWallet.INITIAL_LOAD_TRANSACTION_NUMBER, offset);
      this.transactions = data.data;
      this.transactionOffset = offset;
      this.transactionTotalCount = data.total;
    } finally {
      this.$transactionsLoading = false;
    }
  }

  async cancelTransaction(transactionId) {
    await deleteWalletTransaction(this.accountId, transactionId);
    await this.update();
  }

  setDefaultNumberTransactions() {
    this.transactions = this.transactions.splice(
      0,
      ModelAccountWallet.INITIAL_LOAD_TRANSACTION_NUMBER,
    );
  }
}
